import { BaseModel } from "@/models/products/base";
import { ChairModel } from "@/models/products/chair";
import { Collection, ProductLineName } from "@/models/products/collection";
import { ProductType } from "@/models/products/product";
import { UmbrellaModel } from "@/models/products/umbrella";
import { GenericError } from "@/services/error_service";
import ProductService from "@/services/product_service";
import { Prop, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { StoreModule } from "@/store/types";
import { OrderActions, OrderGetters } from "@/store/modules/order/types";
import { LineItem } from "@/models/order/order";
import { mixins } from "vue-class-component";
import { Mobile } from "@/mixins/mobile";
import { SurchargeStore } from "@/mixins/surcharge";
import { FabricSwatch } from "@/models/products/fabric";
import FabricService from "@/services/fabric_service";

export default class Items extends mixins(Mobile, SurchargeStore) {
  @(namespace(StoreModule.Order).Action(OrderActions.AddLineItem))
  addItem!: (value: LineItem) => Promise<void>;
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrderItems))
  storeItems!: LineItem[];
  @(namespace(StoreModule.Order).Action(OrderActions.EditLineItem))
  editLineItem!: (value: { item: LineItem; index: number }) => Promise<void>;
  protected productService = new ProductService();
  protected fabricService = new FabricService();
  protected editMode = false;
  protected items: any[] = []; // children component will return this properly typed
  @Prop() product!: ProductType;
  @Prop() handle!: ProductLineName;

  protected async getItems(
    product: ProductType,
    handle: ProductLineName
  ): Promise<void> {
    let items;
    try {
      items = await this.productService.getProducts({
        category: product as ProductType,
        pl: handle
      });
    } catch (err) {
      GenericError.popup(err.message);
    }
    this.items = items as any[];
  }

  protected async getItem(
    model: string
  ): Promise<UmbrellaModel | BaseModel | ChairModel> {
    let item;
    try {
      item = await this.productService.getSingleProduct(model);
    } catch (err) {
      GenericError.popup(err.message);
    }
    return item["product"];
  }

  protected async getProductLineInfo(): Promise<Collection> {
    let file;
    try {
      file = await this.productService.getProductLine(this.handle);
    } catch (err) {
      GenericError.popup(err.message);
    }
    return file;
  }

  /**
   * Look up a single fabric swatch from a config-file fabric reference.
   * The reference is either:
   *   - "<mfr_code>"           — bare mfr_code (legacy / non-duplicate)
   *   - "<mfr_code>::<group>"  — disambiguated form for fabrics whose mfr_code
   *     is shared with another fabric document (e.g. SENS D-020 in Coquette
   *     vs. Finesse vs. Enea); "<group>" is the fabric's name.group value.
   * The "::" suffix is stripped before the API call; when present, it's forwarded
   * as the `group` filter so the API returns the correct fabric.
   */
  protected async getFabricObject(fabricRef: string): Promise<FabricSwatch> {
    let swatch = {} as FabricSwatch;
    const sepIdx = fabricRef.indexOf("::");
    const mfrCode = sepIdx === -1 ? fabricRef : fabricRef.substring(0, sepIdx);
    const group = sepIdx === -1 ? undefined : fabricRef.substring(sepIdx + 2);
    try {
      const swatches = await this.fabricService.getSwatchesByCode(
        mfrCode,
        group ? { group } : undefined
      );
      swatch = swatches[0];
    } catch (err) {
      GenericError.popup(err.message);
    }
    return swatch;
  }
}
